import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { modifyUser, createUser } from "../../../store/thunks/userThunk";
import GoogleMapsAutocomplete from "../../../applicationUi/components/GoogleAutoComplete";
import { generateRandomPassword } from "../../../utils/generateRandomPassword";

const UserPersonalInformation = ({ user, onComplete, isEditMode = false }) => {
  const dispatch = useDispatch();
  const [saveStatus, setSaveStatus] = useState("initial");
  const [editMode, setEditMode] = useState(!user);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  console.log("user in userpersonalinformation", user);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phoneNumbers: user?.phoneNumbers || [{ type: "", number: "" }],
      dateOfBirth: user?.dateOfBirth
        ? new Date(user.dateOfBirth).toISOString().split("T")[0]
        : "",
      address: {
        street: user?.address?.street || "",
        city: user?.address?.city || "",
        province: user?.address?.province || "",
        postalCode: user?.address?.postalCode || "",
        country: user?.address?.country || "",
      },
      password: "",
    },

    validationSchema: Yup.object({
      firstName: Yup.string().required("Required"),
      lastName: Yup.string().required("Required"),
      email: Yup.string().email("Invalid email address").required("Required"),
      phoneNumbers: Yup.array().of(
        Yup.object({
          type: Yup.string(),
          number: Yup.string(),
        })
      ),
      dateOfBirth: Yup.date(),
      password: isEditMode ? Yup.string() : Yup.string().required("Required"),
    }),

    onSubmit: async (values) => {
      setSaveStatus("saving");
      let userData = values;

      if (isEditMode) {
        const getChangedFields = (values, original) => {
          const changedFields = {};

          for (const key in values) {
            if (key === "address") {
              const addressChanges = {};
              for (const addressKey in values.address) {
                if (
                  values.address[addressKey] !==
                  (original.address || {})[addressKey]
                ) {
                  addressChanges[addressKey] = values.address[addressKey];
                }
              }
              if (Object.keys(addressChanges).length > 0) {
                changedFields.address = {
                  ...original.address,
                  ...addressChanges,
                };
              }
            } else if (
              JSON.stringify(values[key]) !== JSON.stringify(original[key]) &&
              values[key] !== ""
            ) {
              changedFields[key] = values[key];
            }
          }

          return changedFields;
        };

        userData = getChangedFields(values, user);
      }

      if (isEditMode && !userData.password) {
        delete userData.password;
      }

      const action = isEditMode
        ? modifyUser({ userId: user._id, userData })
        : createUser(userData);

      try {
        const response = await dispatch(action).unwrap();
        setSaveStatus("saved");
        setEditMode(false);

        if (!isEditMode && response?.userId) {
          onComplete(response);
        }

        // Show success modal
        setShowSuccessModal(true);

        // Reset after 2 seconds
        setTimeout(() => {
          setSaveStatus("initial");
          setShowSuccessModal(false);
        }, 2000);
      } catch (error) {
        console.error("Error creating/modifying user:", error);
        setSaveStatus("error");
      }
    },
  });

  const handleGenerateNewPassword = () => {
    const newPassword = generateRandomPassword();
    formik.setFieldValue("password", newPassword);
    console.log("New password generated and set in form");
  };

  const handlePlaceSelected = (place) => {
    const addressComponents = place.address_components;
    let street = "";
    let city = "";
    let province = "";
    let postalCode = "";
    let country = "";

    addressComponents.forEach((component) => {
      const types = component.types;
      if (types.includes("street_number")) {
        street = component.long_name + " ";
      }
      if (types.includes("route")) {
        street += component.long_name;
      }
      if (types.includes("locality")) {
        city = component.long_name;
      }
      if (types.includes("administrative_area_level_1")) {
        province = component.long_name;
      }
      if (types.includes("postal_code")) {
        postalCode = component.long_name;
      }
      if (types.includes("country")) {
        country = component.long_name;
      }
    });

    formik.setFieldValue("address", {
      street,
      city,
      province,
      postalCode,
      country,
    });

    console.log("set this postal code:", postalCode);
  };

  const handleAddPhoneNumber = () => {
    formik.setFieldValue("phoneNumbers", [
      ...formik.values.phoneNumbers,
      { type: "", number: "" },
    ]);
  };

  const handleRemovePhoneNumber = (index) => {
    const newPhoneNumbers = [...formik.values.phoneNumbers];
    newPhoneNumbers.splice(index, 1);
    formik.setFieldValue("phoneNumbers", newPhoneNumbers);
  };

  return (
    <div className="space-y-6">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-green-600"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Success</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {formik.values.firstName} {formik.values.lastName} updated
                  successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with Edit Toggle */}
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            Personal Information
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Basic user details and contact information
          </p>
        </div>
        {user && (
          <button
            type="button"
            onClick={() => setEditMode(!editMode)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors">
            {editMode ? "Cancel" : "Edit"}
          </button>
        )}
      </div>

      <form onSubmit={formik.handleSubmit} className="space-y-4">
        {/* Basic Information */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-1">
              First name *
            </label>
            <input
              type="text"
              name="firstName"
              id="firstName"
              disabled={!editMode}
              autoComplete="off"
              {...formik.getFieldProps("firstName")}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <p className="text-red-600 text-xs mt-1">
                {formik.errors.firstName}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-1">
              Last name *
            </label>
            <input
              type="text"
              name="lastName"
              id="lastName"
              disabled={!editMode}
              autoComplete="off"
              {...formik.getFieldProps("lastName")}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <p className="text-red-600 text-xs mt-1">
                {formik.errors.lastName}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1">
              Email address *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              disabled={!editMode}
              autoComplete="off"
              {...formik.getFieldProps("email")}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            {formik.touched.email && formik.errors.email && (
              <p className="text-red-600 text-xs mt-1">{formik.errors.email}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="dateOfBirth"
              className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              name="dateOfBirth"
              id="dateOfBirth"
              disabled={!editMode}
              autoComplete="off"
              {...formik.getFieldProps("dateOfBirth")}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            {formik.touched.dateOfBirth && formik.errors.dateOfBirth && (
              <p className="text-red-600 text-xs mt-1">
                {formik.errors.dateOfBirth}
              </p>
            )}
          </div>
        </div>

        {/* Password Section */}
        {editMode && (
          <div className="bg-gray-50 border border-gray-200 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Password</h3>
              <button
                type="button"
                onClick={handleGenerateNewPassword}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-sm text-white bg-gray-900 hover:bg-gray-800 transition-colors">
                Generate Password
              </button>
            </div>
            {formik.values.password && (
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1">
                  Generated Password
                </label>
                <input
                  type="text"
                  id="password"
                  name="password"
                  {...formik.getFieldProps("password")}
                  readOnly
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm bg-white font-mono"
                />
              </div>
            )}
          </div>
        )}

        {/* Phone Numbers */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Phone Numbers
            </label>
            {editMode && (
              <button
                type="button"
                onClick={handleAddPhoneNumber}
                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
                Add Phone
              </button>
            )}
          </div>
          <div className="space-y-2">
            {formik.values.phoneNumbers &&
              formik.values.phoneNumbers.map((phone, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <select
                    name={`phoneNumbers[${index}].type`}
                    value={phone.type}
                    disabled={!editMode}
                    onChange={formik.handleChange}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500">
                    <option value="">Type</option>
                    <option value="Home">Home</option>
                    <option value="Work">Work</option>
                    <option value="Mobile">Mobile</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    type="text"
                    name={`phoneNumbers[${index}].number`}
                    value={phone.number}
                    disabled={!editMode}
                    onChange={formik.handleChange}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="Phone number"
                  />
                  {editMode && (
                    <button
                      type="button"
                      onClick={() => handleRemovePhoneNumber(index)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm text-red-700 bg-red-50 hover:bg-red-100 transition-colors">
                      Remove
                    </button>
                  )}
                </div>
              ))}
          </div>
          {formik.touched.phoneNumbers && formik.errors.phoneNumbers && (
            <p className="text-red-600 text-xs mt-1">
              {formik.errors.phoneNumbers}
            </p>
          )}
        </div>

        {/* Address Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Address</h3>

          {editMode && (
            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700 mb-1">
                Search Address
              </label>
              <GoogleMapsAutocomplete
                onPlaceSelected={handlePlaceSelected}
                inputId="address"
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-3">
              <label
                htmlFor="street"
                className="block text-sm font-medium text-gray-700 mb-1">
                Street address
              </label>
              <input
                type="text"
                name="street"
                id="street"
                disabled={!editMode}
                autoComplete="off"
                {...formik.getFieldProps("address.street")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            <div>
              <label
                htmlFor="city"
                className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                name="city"
                id="city"
                disabled={!editMode}
                autoComplete="off"
                {...formik.getFieldProps("address.city")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            <div>
              <label
                htmlFor="province"
                className="block text-sm font-medium text-gray-700 mb-1">
                Province
              </label>
              <input
                type="text"
                name="province"
                id="province"
                disabled={!editMode}
                autoComplete="off"
                {...formik.getFieldProps("address.province")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            <div>
              <label
                htmlFor="postalCode"
                className="block text-sm font-medium text-gray-700 mb-1">
                Postal code
              </label>
              <input
                type="text"
                name="postalCode"
                id="postalCode"
                disabled={!editMode}
                autoComplete="off"
                {...formik.getFieldProps("address.postalCode")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <select
              id="country"
              name="country"
              disabled={!editMode}
              autoComplete="off"
              {...formik.getFieldProps("address.country")}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 sm:max-w-xs">
              <option value="">Select a country</option>
              <option value="United States">United States</option>
              <option value="Canada">Canada</option>
              <option value="Mexico">Mexico</option>
            </select>
          </div>
        </div>

        {/* Save Button */}
        {editMode && (
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <div className="flex flex-col items-end space-y-2">
              <button
                type="submit"
                disabled={saveStatus === "saving"}
                className={`inline-flex justify-center px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                  saveStatus === "saving"
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : saveStatus === "saved"
                    ? "bg-green-600 text-white"
                    : saveStatus === "duplicate-id" || saveStatus === "error"
                    ? "bg-red-600 text-white"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                }`}>
                {saveStatus === "saving"
                  ? "Saving..."
                  : saveStatus === "saved"
                  ? "Saved!"
                  : saveStatus === "duplicate-id"
                  ? "Error: Duplicate ID"
                  : saveStatus === "error"
                  ? "Error"
                  : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default UserPersonalInformation;
